import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { LogIn, LogOut, Clock, Utensils, Camera, MapPin, Shield, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function BadgePage() {
  const { user, logout } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [badges, setBadges] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showCamera, setShowCamera] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [selfieData, setSelfieData] = useState('');
  const [geoStatus, setGeoStatus] = useState('');
  const [location, setLocation] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const { data: emp } = await api.get('/employees/me');
      setEmployee(emp);
      const { data: bdgs } = await api.get('/employees/me/badges?limit=14');
      setBadges(bdgs);
      const today = new Date().toISOString().split('T')[0];
      setIsClockedIn(!!bdgs.find(b => b.date === today && !b.clock_out));
    } catch {
      setEmployee(null);
    }
    try {
      const { data: s } = await api.get('/settings');
      setSettings(s);
    } catch {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const getLocation = () => new Promise((resolve, reject) => {
    setGeoStatus('Localisation en cours...');
    if (!navigator.geolocation) { reject(new Error('Geolocalisation non disponible')); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeoStatus('Position trouvee'); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
      (err) => { setGeoStatus('Erreur de localisation'); reject(err); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });

  const requiresSelfie = settings?.badge_require_selfie !== false;
  const requiresGeolocation = settings?.badge_require_geolocation !== false;

  const submitBadge = async (action, payload, successMessage) => {
    await api.post(`/employees/badge/${action}`, payload);
    toast.success(successMessage);
    await loadData();
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 320, height: 240 } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; }
    } catch (err) { toast.error('Impossible d\'acceder a la camera'); setShowCamera(false); }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = 320; canvas.height = 240;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0, 320, 240);
    const data = canvas.toDataURL('image/jpeg', 0.5);
    setSelfieData(data);
    stopCamera();
  };

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  };

  const initBadge = async (action) => {
    setPendingAction(action);
    setSelfieData('');
    setGeoStatus('');
    setLocation(null);
    let loc = null;
    if (!requiresSelfie && !requiresGeolocation) {
      setLoading(true);
      try {
        await submitBadge(action, { selfie: '', latitude: 0, longitude: 0 }, action === 'in' ? 'Arrivee enregistree !' : 'Depart enregistre !');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erreur de pointage');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (requiresGeolocation) {
      try {
        loc = await getLocation();
        setLocation(loc);
      } catch {
        toast.error('Impossible d\'obtenir votre position. Activez la geolocalisation.');
        return;
      }
    }

    if (requiresSelfie) {
      setShowCamera(true);
      await startCamera();
      return;
    }

    setLoading(true);
    try {
      const payload = { selfie: '', latitude: loc?.lat || 0, longitude: loc?.lng || 0 };
      await submitBadge(action, payload, action === 'in' ? 'Arrivee enregistree !' : 'Depart enregistre !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de pointage');
    } finally {
      setLoading(false);
    }
  };

  const confirmBadge = async () => {
    if (requiresSelfie && !selfieData) { toast.error('Prenez une photo d\'abord'); return; }
    setLoading(true);
    setShowCamera(false);
    stopCamera();
    try {
      const payload = {
        selfie: requiresSelfie ? selfieData : '',
        latitude: requiresGeolocation ? (location?.lat || 0) : 0,
        longitude: requiresGeolocation ? (location?.lng || 0) : 0
      };
      if (pendingAction === 'in') {
        await submitBadge('in', payload, 'Arrivee enregistree !');
      } else {
        const { data } = await api.post('/employees/badge/out', payload);
        toast.success(`Depart enregistre ! ${data.hours_worked}h travaillees`);
        await loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de pointage');
    } finally { setLoading(false); }
  };

  useEffect(() => { return () => stopCamera(); }, []);

  if (!employee) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-[#7A7A75] mb-4">Profil employe non trouve. Contactez votre administrateur.</p>
          <a href="/login"><Button variant="outline" className="rounded-full">Retour</Button></a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="badge-page">
      <div className="bg-[#2A2A28] text-[#FAF8F5] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Utensils className="w-5 h-5 text-[#D4AF37]" />
          <span className="font-heading text-sm font-semibold">Le Traiteur Libanais</span>
        </div>
        <button onClick={async () => { await logout(); window.location.href = '/login'; }} className="text-xs text-[#FAF8F5]/60 hover:text-white flex items-center gap-1" data-testid="badge-logout">
          <LogOut className="w-3.5 h-3.5" /> Deconnexion
        </button>
      </div>

      <div className="max-w-md mx-auto px-4 py-8">
        <div className="text-center mb-4">
          <h2 className="font-heading text-xl font-semibold text-[#2A2A28]">{employee.first_name} {employee.last_name}</h2>
          <p className="text-sm text-[#7A7A75]">{employee.contract_type} - {employee.hours_per_week}h/semaine</p>
        </div>

        {/* Security indicators */}
        <div className="flex justify-center gap-3 mb-6 flex-wrap">
          <Badge className={`${requiresSelfie ? 'bg-[#5B6B3F]/10 text-[#5B6B3F]' : 'bg-[#7A7A75]/10 text-[#7A7A75]'} text-xs`}>
            <Camera className="w-3 h-3 mr-1" />{requiresSelfie ? 'Selfie requis' : 'Selfie optionnel'}
          </Badge>
          <Badge className={`${requiresGeolocation ? 'bg-[#5B6B3F]/10 text-[#5B6B3F]' : 'bg-[#7A7A75]/10 text-[#7A7A75]'} text-xs`}>
            <MapPin className="w-3 h-3 mr-1" />{requiresGeolocation ? 'Geolocalisation requise' : 'Geolocalisation optionnelle'}
          </Badge>
          <Badge className="bg-[#5B6B3F]/10 text-[#5B6B3F] text-xs"><Shield className="w-3 h-3 mr-1" />Securise</Badge>
        </div>

        {/* Clock */}
        <div className="text-center mb-8">
          <p className="font-heading text-5xl font-bold text-[#2A2A28] tracking-tight">
            {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </p>
          <p className="text-sm text-[#7A7A75] mt-1">
            {currentTime.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Badge Button */}
        <div className="flex justify-center mb-8">
          <button
            onClick={() => initBadge(isClockedIn ? 'out' : 'in')}
            disabled={loading}
            className={`w-40 h-40 rounded-full flex flex-col items-center justify-center gap-2 text-white font-medium text-lg shadow-lg active:scale-95 transition-all ${
              isClockedIn ? 'bg-[#8C3A3A] hover:bg-[#7a3232] shadow-[#8C3A3A]/30' : 'bg-[#5B6B3F] hover:bg-[#4A5832] shadow-[#5B6B3F]/30'
            }`}
            data-testid="badge-button"
          >
            {isClockedIn ? <LogOut className="w-8 h-8" /> : <LogIn className="w-8 h-8" />}
            {loading ? '...' : isClockedIn ? 'Depart' : 'Arrivee'}
          </button>
        </div>

        <div className="text-center mb-8">
          <Badge className={`text-sm px-4 py-1 ${isClockedIn ? 'bg-[#5B6B3F]/10 text-[#5B6B3F]' : 'bg-[#7A7A75]/10 text-[#7A7A75]'}`}>
            {isClockedIn ? 'En service' : 'Hors service'}
          </Badge>
        </div>

        {/* Recent Badges */}
        <div className="bg-white rounded-xl border border-[#EBE8E0] overflow-hidden">
          <div className="p-4 border-b border-[#EBE8E0]">
            <h3 className="font-medium text-[#2A2A28] flex items-center gap-1"><Clock className="w-4 h-4" /> Historique recent</h3>
          </div>
          <div className="divide-y divide-[#EBE8E0]">
            {badges.map(b => (
              <div key={b.id} className="p-3 flex items-center justify-between text-sm" data-testid={`badge-entry-${b.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[#2A2A28]">{b.date}</span>
                  {b.selfie_in && <Camera className="w-3 h-3 text-[#5B6B3F]" title="Selfie verifie" />}
                  {b.admin_closed && <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] text-xs">Admin</Badge>}
                </div>
                <div className="flex gap-4 text-[#7A7A75]">
                  <span>{b.clock_in ? new Date(b.clock_in).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                  <span>{b.clock_out ? new Date(b.clock_out).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                  <span className="font-medium text-[#2A2A28]">{b.hours_worked ? `${b.hours_worked}h` : '-'}</span>
                </div>
              </div>
            ))}
            {badges.length === 0 && <p className="p-4 text-center text-sm text-[#7A7A75]">Aucun pointage</p>}
          </div>
        </div>
      </div>

      {/* Camera Dialog */}
      <Dialog open={showCamera} onOpenChange={(v) => { if (!v) { stopCamera(); setShowCamera(false); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#5B6B3F]" />
              Verification d'identite
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {requiresGeolocation && geoStatus && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-[#5B6B3F]" />
                <span className={location ? 'text-[#5B6B3F]' : 'text-[#D4AF37]'}>{geoStatus}</span>
              </div>
            )}

            {requiresSelfie && !selfieData ? (
              <div className="space-y-3">
                <div className="bg-black rounded-xl overflow-hidden aspect-[4/3]">
                  <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
                </div>
                <Button onClick={capturePhoto} className="w-full rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white" data-testid="capture-selfie">
                  <Camera className="w-4 h-4 mr-2" /> Prendre la photo
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {requiresSelfie && (
                  <div className="rounded-xl overflow-hidden border border-[#EBE8E0]">
                    <img src={selfieData} alt="Selfie" className="w-full" style={{ transform: 'scaleX(-1)' }} />
                  </div>
                )}
                <div className="flex gap-2">
                  {requiresSelfie && (
                    <Button variant="outline" onClick={() => { setSelfieData(''); startCamera(); }} className="flex-1 rounded-xl border-[#EBE8E0]" data-testid="retake-selfie">
                      Reprendre
                    </Button>
                  )}
                  <Button onClick={confirmBadge} disabled={loading} className="flex-1 rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white" data-testid="confirm-badge">
                    {loading ? '...' : 'Confirmer'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
